import { CouponRow, PriceRow } from 'server/types';
import { MiraseeCheckoutConfig } from 'server/types/pmts-overrides';
import { PurchaseFn, StripeHandlerFn } from 'shared/payments/types';
import { CheckoutFormWizard } from './checkout-form-wizard';
import { useDocumentTitle } from 'client/utils/use-document-title';

type Props = {
  config: MiraseeCheckoutConfig;
  course: { id: string; title: string };
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
};

function CheckIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      style="flex-shrink: 0; margin-top: 2px"
    >
      <circle cx="13" cy="13" r="13" fill="#16a34a" />
      <path
        d="M7.5 13l3.5 3.5 7-7"
        stroke="#fff"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function MiraseeCheckoutTemplate({
  config,
  course,
  price,
  coupon,
  collectAddress,
  handleStripe,
  beginPurchase,
  onPurchaseComplete,
}: Props) {
  useDocumentTitle([course.title, 'Checkout']);

  const { hero, benefits, testimonials } = config;

  return (
    <div
      class="mirasee-checkout"
      style="min-height: 100vh; background: linear-gradient(to bottom, #f0f2f5 85%, #ffffff 100%); font-family: 'Montserrat', system-ui, -apple-system, sans-serif"
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .mirasee-checkout .bg-blue-600 { background-color: #013EA6; }
        .mirasee-checkout .text-blue-600 { color: #013EA6; }
        .mirasee-checkout .text-blue-700 { color: #013EA6; }
      `,
        }}
      />

      {/* Hero */}
      <div style="position: relative; padding: 60px 20px 340px; text-align: center; background: linear-gradient(135deg, #0077cc 0%, #0052b4 35%, #002d8a 70%, #001a6e 100%)">
        <svg
          viewBox="0 0 1440 360"
          preserveAspectRatio="none"
          style="position: absolute; bottom: -1px; left: 0; width: 100%; height: 360px; pointer-events: none"
        >
          <path d="M0,360 L0,100 C480,130 960,230 1440,260 L1440,360 Z" fill="#f0f2f5" />
        </svg>
        {hero.logoUrl && (
          <div style="margin-bottom: 24px">
            <img src={hero.logoUrl} alt="" style="max-height: 50px; display: inline-block" />
          </div>
        )}
        <h1 style="color: #fff; font-size: clamp(26px, 4.5vw, 38px); font-weight: 800; line-height: 1.2; margin: 0 auto; max-width: 1000px">
          {hero.headline}
        </h1>
        {hero.subtitle && (
          <p style="color: rgba(255,255,255,0.92); font-size: clamp(16px, 2.2vw, 21px); font-weight: 600; margin: 24px auto 0; max-width: 920px; line-height: 1.5">
            {hero.subtitle}
          </p>
        )}

        {config.priceBadgeText && (
          <div style="margin-top: 32px; text-align: center">
            <span style="display: inline-block; padding: 16px 50px; border-radius: 999px; background: #ffd437; color: #1a1a1a; font-size: 20px; font-weight: 500; box-shadow: 0 2px 8px rgba(0,0,0,0.15); white-space: nowrap">
              {config.priceBadgeText}
            </span>
          </div>
        )}
      </div>

      {/* Main content card */}
      <div style="max-width: 1100px; margin: -280px auto 0; padding: 0 20px; position: relative; z-index: 1">
        <div style="background: #fff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); overflow: hidden">
          <div style="display: flex; flex-wrap: wrap">
            {/* Left column */}
            <div style="flex: 2; min-width: 0; padding: 32px 36px 36px">
              {benefits && (
                <div style="margin-bottom: 32px">
                  {benefits.heading && (
                    <h2 style="font-size: 22px; font-weight: 800; color: #013EA6; margin: 0 0 22px">
                      {benefits.heading}
                    </h2>
                  )}
                  <div style="display: flex; flex-direction: column; gap: 22px">
                    {benefits.items.map((item, i) => (
                      <div key={i} style="display: flex; gap: 14px; align-items: flex-start">
                        <CheckIcon />
                        <span style="font-size: 16px; line-height: 1.6; color: #4b5563">
                          <strong style="font-weight: 800; color: #1a1a1a">{item.bold}</strong>
                          {item.text && <span style="font-weight: 400"> {item.text}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style="border: 1px solid #e5e7eb; border-radius: 14px; padding: 28px 24px; background: linear-gradient(165deg, #fafbff 0%, #f5f7fa 100%); box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(1,62,166,0.06)">
                <CheckoutFormWizard
                  courseId={course.id}
                  price={price}
                  coupon={coupon}
                  collectAddress={collectAddress}
                  handleStripe={handleStripe}
                  beginPurchase={beginPurchase}
                  onPurchaseComplete={onPurchaseComplete}
                  pwywVariant="box"
                />
              </div>

              {config.termsHtml && (
                <div
                  style="margin-top: 20px; font-size: 12px; color: #6b7280; line-height: 1.6"
                  dangerouslySetInnerHTML={{ __html: config.termsHtml }}
                />
              )}
            </div>

            {/* Right column — testimonials */}
            {testimonials && testimonials.length > 0 && (
              <div style="flex: 0 0 320px; min-width: 280px; background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%); border-left: 1px solid #e2e8f0; padding: 28px 24px; display: flex; flex-direction: column">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    style={`padding: 18px 0${i > 0 ? '; border-top: 1px solid #e2e8f0' : ''}`}
                  >
                    <div style="display: flex; gap: 12px; align-items: flex-start">
                      {t.photoUrl ? (
                        <img
                          src={t.photoUrl}
                          alt=""
                          style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0"
                        />
                      ) : (
                        <div style="width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #013EA6, #0089BD); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 15px; font-weight: 700; flex-shrink: 0">
                          {t.name.charAt(0)}
                        </div>
                      )}
                      <div style="min-width: 0">
                        <div style="font-size: 13px; font-weight: 700; color: #1e293b">
                          {t.name}
                        </div>
                        {t.title && (
                          <div style="font-size: 11px; color: #64748b; font-weight: 500; margin-bottom: 6px">
                            {t.title}
                          </div>
                        )}
                      </div>
                    </div>
                    <p style="font-size: 13.5px; line-height: 1.6; color: #475569; margin: 10px 0 0; font-style: italic">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {(config.contactPhone || config.contactEmail) && (
          <div style="text-align: center; margin-top: 20px; font-size: 13px; color: #6b7280">
            {config.contactPhone && <span>Phone: {config.contactPhone}</span>}
            {config.contactPhone && config.contactEmail && <span> | </span>}
            {config.contactEmail && (
              <span>
                Email:{' '}
                <a href={`mailto:${config.contactEmail}`} style="color: #2563eb">
                  {config.contactEmail}
                </a>
              </span>
            )}
          </div>
        )}
      </div>

      {(config.disclaimerHtml || config.footerLogoUrl) && (
        <div style="max-width: 1100px; margin: 32px auto 40px; padding: 0 20px; text-align: center">
          {config.footerLogoUrl && (
            <div style="margin-bottom: 12px">
              <img
                src={config.footerLogoUrl}
                alt=""
                style="max-height: 30px; display: inline-block; opacity: 0.6"
              />
            </div>
          )}
          {config.disclaimerHtml && (
            <div
              style="font-size: 11px; color: #9ca3af; line-height: 1.6; max-width: 800px; margin: 0 auto"
              dangerouslySetInnerHTML={{ __html: config.disclaimerHtml }}
            />
          )}
        </div>
      )}
    </div>
  );
}
